import React from "react";
import { useState, useEffect } from "react";
import { api } from "../../api/client";

const TabCars = () => {
  const [cars, setCars] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [carForm, setCarForm] = useState({
    name: "",
    number: "",
    capacity: 14,
    driverId: "",
  });
  const [bulkSelectedCars, setBulkSelectedCars] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCars, setFilteredCars] = useState([]);

  const load = async () => {
    const c = await api("/admin/cars");
    setCars(c.cars);
    setFilteredCars(c.cars);
    setLoadingDrivers(true);
    const d = await api("/drivers/list");
    setDrivers(d.drivers);
    setLoadingDrivers(false);
  };
  useEffect(() => {
    load();
  }, []);

  const createCar = async (e) => {
    e.preventDefault();
    await api("/admin/cars", {
      method: "POST",
      body: {
        name: carForm.name,
        number: carForm.number,
        capacity: carForm.capacity,
        driverId: carForm.driverId,
      },
    });
    setCarForm({ name: "", number: "", capacity: 14, driverId: "" });
    load();
  };

  const bulkDeleteCars = async () => {
    if (bulkSelectedCars.length === 0) {
      alert("No vehicles selected");
      return;
    }
    if (window.confirm(`Delete ${bulkSelectedCars.length} vehicle(s)?`)) {
      try {
        await api("/admin/cars/bulk-delete", {
          method: "POST",
          body: { carIds: bulkSelectedCars },
        });
        setBulkSelectedCars([]);
        load();
      } catch (err) {
        alert(err.message || "Failed to delete vehicles");
      }
    }
  };

  const toggleCarSelection = (carId) => {
    setBulkSelectedCars((prev) =>
      prev.includes(carId)
        ? prev.filter((id) => id !== carId)
        : [...prev, carId]
    );
  };

  const toggleSelectAll = () => {
    const allCarIds = filteredCars.map((c) => c._id);
    const allSelected = allCarIds.every((id) => bulkSelectedCars.includes(id));

    if (allSelected) {
      setBulkSelectedCars([]);
    } else {
      setBulkSelectedCars(allCarIds);
    }
  };

  return (
    <div className="card">
      <h4>Current Vehicle List</h4>
      <div style={{ marginBottom: "1rem" }}>
        <input
          type="text"
          placeholder="Search by vehicle number..."
          value={searchTerm}
          onChange={(e) => {
            const term = e.target.value.toLowerCase();
            setSearchTerm(term);
            if (term.trim() === "") {
              setFilteredCars(cars);
            } else {
              const filtered = cars.filter((c) =>
                c.number.toLowerCase().includes(term)
              );
              setFilteredCars(filtered);
            }
          }}
          style={{
            width: "100%",
            maxWidth: "400px",
            padding: "0.5rem",
            fontSize: "0.875rem",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
          }}
        />
      </div>

      {bulkSelectedCars.length > 0 && (
        <div style={{ marginBottom: "1rem", display: "flex", gap: "1rem", alignItems: "center" }}>
          <span style={{ fontWeight: 600, color: "#2d7a64" }}>
            {bulkSelectedCars.length} selected
          </span>
          <button
            className="btn danger"
            onClick={bulkDeleteCars}
          >
            Delete Selected
          </button>
          <button
            className="btn secondary"
            onClick={() => setBulkSelectedCars([])}
          >
            Clear Selection
          </button>
        </div>
      )}

      <div className="scroll-x">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: "50px" }}>
                <input
                  type="checkbox"
                  checked={
                    filteredCars.length > 0 &&
                    filteredCars.every((c) => bulkSelectedCars.includes(c._id))
                  }
                  onChange={toggleSelectAll}
                  style={{ cursor: "pointer" }}
                />
              </th>
              <th>Name</th>
              <th>Number</th>
              <th>Capacity</th>
              <th>Driver</th>
              {bulkSelectedCars.length === 0 && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredCars.map((c) => (
              <CarRow
                key={c._id}
                car={c}
                drivers={drivers}
                onEdit={async (id, data) => {
                  await api(`/admin/cars/${id}/edit`, {
                    method: "POST",
                    body: data,
                  });
                  load();
                }}
                onDelete={async (id) => {
                  if (window.confirm("Delete vehicle?")) {
                    await api(`/admin/cars/${id}`, { method: "DELETE" });
                    load();
                  }
                }}
                isSelected={bulkSelectedCars.includes(c._id)}
                onToggleSelect={toggleCarSelection}
                hasBulkSelection={bulkSelectedCars.length > 0}
              />
            ))}
            {filteredCars.length === 0 && (
              <tr>
                <td colSpan={6}>No vehicles</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="divider" />
      <h3>Add New Vehicle</h3>
      <form
        onSubmit={createCar}
        className="input-row"
        style={{ gap: 12, marginBottom: 10 }}
      >
        <input
          placeholder="Vehicle Name e.g. Coaster, Haice"
          value={carForm.name}
          onChange={(e) => setCarForm((f) => ({ ...f, name: e.target.value }))}
          required
        />
        <input
          placeholder="Vehicle Number"
          value={carForm.number}
          onChange={(e) =>
            setCarForm((f) => ({ ...f, number: e.target.value }))
          }
          required
        />
        <input
          type="number"
          placeholder="Capacity"
          value={carForm.capacity}
          onChange={(e) =>
            setCarForm((f) => ({
              ...f,
              capacity: Number(e.target.value),
            }))
          }
          min="1"
          max="100"
          required
        />
        <select
          value={carForm.driverId}
          onChange={(e) =>
            setCarForm((f) => ({ ...f, driverId: e.target.value }))
          }
          required
        >
          <option value="">
            {loadingDrivers ? "Loading drivers..." : "Select Driver"}
          </option>
          {drivers.map((d) => (
            <option key={d._id} value={d._id}>
              {d.name} ({d.email})
            </option>
          ))}
        </select>
        <button className="btn success" type="submit">
          Add Vehicle
        </button>
      </form>
    </div>
  );
};

function CarRow({ car, drivers, onEdit, onDelete, isSelected, onToggleSelect, hasBulkSelection }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    name: car.name,
    number: car.number,
    capacity: car.capacity,
    driverId: car.driver?._id || "",
  });
  const save = async () => {
    await onEdit(car._id, form);
    setEdit(false);
  };
  return (
    <tr>
      <td>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(car._id)}
          style={{ cursor: "pointer" }}
        />
      </td>
      <td>
        {edit ? (
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            style={{ width: "90px" }}
          />
        ) : (
          car.name
        )}
      </td>
      <td>
        {edit ? (
          <input
            value={form.number}
            onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))}
            style={{ width: "90px" }}
          />
        ) : (
          car.number
        )}
      </td>
      <td>
        {edit ? (
          <input
            type="number"
            value={form.capacity}
            onChange={(e) =>
              setForm((f) => ({ ...f, capacity: Number(e.target.value) }))
            }
            style={{ width: "90px" }}
            min="1"
            max="100"
            required
          />
        ) : (
          car.capacity
        )}
      </td>
      <td>
        {edit ? (
          <select
            value={form.driverId}
            onChange={(e) =>
              setForm((f) => ({ ...f, driverId: e.target.value }))
            }
          >
            <option value="">Select Driver</option>
            {drivers.map((d) => (
              <option key={d._id} value={d._id}>
                {d.name} ({d.email})
              </option>
            ))}
          </select>
        ) : (
          car.driver?.name || "Unassigned"
        )}
      </td>
      {!hasBulkSelection && (
        <td>
          <span style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
            {edit ? (
              <>
                <button
                  className="btn success"
                  style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem" }}
                  onClick={save}
                >
                  Save
                </button>
                <button
                  className="btn secondary"
                  style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem" }}
                  onClick={() => setEdit(false)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn"
                  style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem" }}
                  onClick={() => setEdit(true)}
                >
                  Edit
                </button>
                <button
                  className="btn danger"
                  style={{ fontSize: "0.8rem", padding: "0.3rem 0.7rem" }}
                  onClick={() => onDelete(car._id)}
                >
                  Delete
                </button>
              </>
            )}
          </span>
        </td>
      )}
    </tr>
  );
}

export default TabCars;
